import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Pets as PetsIcon,
  HelpOutline as HelpOutlineIcon,
} from "@material-ui/icons";
import { useMerchant } from "../../../store/global/useMerchant";
import validator from "validator";

// Base Page
import BasePage from "../../../pages/basePage";
import { toast } from 'react-toastify'
// Components
import TicketDetails from "./TicketDetails";
import Filters from "./Filters";
import FAQ, { getStatusDescription } from "./FAQ";
import SearchCPFComponent from "./SearchCPFComponent";
import SubscriptionSubcategoryCards from "./SubscriptionSubcategoryCards";

// Utils
import {
  priorityOptions,
  getStatusColor,
  getPriorityColor,
  getPriorityFlagColor,
  formatDate
} from "./utils";

// API
import {
  getSupportTickets,
  createSupportTicket,
  getOptionsClickup,
  closeTicket
} from "../../../services/support";

// Assets
import suporteImage from "../../../assets/atendimento.png";
import { useQueryParam } from "use-query-params";
import { Headset } from "lucide-react";

const getInitialFormData = () => ({
  userName: "",
  email: "",
  whatsapp: "",
  cpf: "",
  category: "",
  subcategory: "",
  classification: "",
  priority: "media",
  status: "aberto",
  title: "",
  description: "",
  attachments: [],
});

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    color: "#F97B18",
    margin: 0,
  },
  newTicketButton: {
    backgroundColor: "#F97B18",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#D05D02",
    },
    "& .MuiButton-startIcon": {
      display: "flex",
      alignItems: "center",
    },
  },
  ticketCard: {
    marginBottom: theme.spacing(2),
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "1px solid #E8EAF6",
    borderRadius: "16px",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    "&:hover": {
      boxShadow: "0 12px 32px rgba(249, 123, 24, 0.15)",
      transform: "translateY(-4px)",
      border: "1px solid #F97B18",
      backgroundColor: "#fafbff",
    },
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  priorityChip: {
    marginLeft: theme.spacing(1),
  },
  dialogContent: {
    width: "100%",
  },
  dialogTitle: {
    backgroundColor: "#F97B18",
    color: "#fff",
    "& .MuiIconButton-root": {
      color: "#fff",
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#E0E0E0",
      },
      "&:hover fieldset": {
        borderColor: "#F97B18",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F97B18",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#F97B18",
    },
  },
  ticketList: {
    marginTop: 15,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  attachmentSection: {
    marginTop: theme.spacing(2),
  },
  attachmentList: {
    marginTop: theme.spacing(1),
  },
  attachmentItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    marginBottom: theme.spacing(1),
  },
  fileInput: {
    display: "none",
  },
  uploadButton: {
    marginTop: theme.spacing(1),
    color: "#F97B18",
    borderColor: "#F97B18",
  },
  headerImage: {
    width: 100,
    height: 100,
    marginRight: theme.spacing(1),
    objectFit: "contain",
    flexShrink: 0,
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 0 rgba(40, 167, 69, 0.7)",
    },
    "50%": {
      transform: "scale(1.05)",
      boxShadow: "0 0 0 10px rgba(40, 167, 69, 0)",
    },
    "100%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 0 rgba(40, 167, 69, 0)",
    },
  },
  "@keyframes pulseRed": {
    "0%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 0 rgba(220, 53, 69, 0.7)",
    },
    "50%": {
      transform: "scale(1.05)",
      boxShadow: "0 0 0 10px rgba(220, 53, 69, 0)",
    },
    "100%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 0 rgba(220, 53, 69, 0)",
    },
  },
  "@keyframes shake": {
    "0%, 100%": {
      transform: "translateX(0)",
    },
    "10%, 30%, 50%, 70%, 90%": {
      transform: "translateX(-3px)",
    },
    "20%, 40%, 60%, 80%": {
      transform: "translateX(3px)",
    },
  },
  validationButton: {
    animation: "$pulse 2s ease-in-out infinite",
    "&:hover": {
      animation: "none",
    },
  },
  validationButtonRed: {
    animation: "$pulseRed 2s ease-in-out infinite",
    "&:hover": {
      animation: "none",
    },
  },
}));



function Suporte() {
  const classes = useStyles();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merchantLoading, setMerchantLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 20,
    total: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    totalPages: 0
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [formData, setFormData] = useState(getInitialFormData());
  const [supportStep, setSupportStep] = useState("category");
  const [formError, setFormError] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [classificationOptions, setClassificationOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [filters, setFilters] = useState({
    keyword: "",
    status: null,
    category: null
  });
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [createTicketLoading, setCreateTicketLoading] = useState(false);
  const merchant = useMerchant(st => st.data.merchant);
  const [closeTicketModal, setCloseTicketModal] = useState({
    open: false,
    ticket: null,
    reason: '',
    loading: false,
    isResolved: true
  });
  const [faqOpen, setFaqOpen] = useState(false);

  const [activeTicket, setActiveTicket] = useQueryParam("ticket");
  const [newTicket, setNewTicket] = useQueryParam("new");

  useEffect(() => {
    // Aguardar merchant estar carregado antes de fazer as primeiras consultas
    if (merchant && merchant.id) {
      setMerchantLoading(false);
      loadClickUpOptions();
      loadTickets(0, filters);
    } else if (merchant === false) {
      // Se merchant é explicitamente false, não está carregado ainda      setMerchantLoading(true);
    }
  }, [merchant]);


  useEffect(() => {
    if (activeTicket) {
      setSelectedTicket(tickets.find(t => t.id === activeTicket))
      setNewTicket(true)
    }
  }, [activeTicket, tickets]);


  const loadClickUpOptions = async () => {
    try {

      setOptionsLoading(true);
      // sem limpar aqui, o aviso continuaria na tela mesmo depois do retry dar certo
      setOptionsError("");

      const response = await getOptionsClickup();
      // Verificar diferentes estruturas possíveis
      let options = [];

      if (response?.connection && Array.isArray(response.connection)) {
        options = response.connection;
      } else if (response?.data?.connection && Array.isArray(response.data.connection)) {
        options = response.data.connection;
      } else if (Array.isArray(response)) {
        options = response;
      } else if (response?.data && Array.isArray(response.data)) {
        options = response.data;
      }

      if (options && Array.isArray(options)) {
        // Processar as opções do ClickUp
        options.forEach(option => {
          if (option.name === "Categoria" && option.type_config?.options) {
            const categories = option.type_config.options
              .filter(cat => cat.name !== "Projetos")
              .map(cat => ({
                value: cat.id,
                label: cat.name,
                color: cat.color,
                orderindex: cat.orderindex
              }));
            setCategoryOptions(categories);
          } else if (option.name === "Classificação" && option.type_config?.options) {
            const classifications = option.type_config.options.map(cls => ({
              value: cls.id,
              label: cls.name,
              color: cls.color,
              orderindex: cls.orderindex
            })); setClassificationOptions(classifications);
          }
        });
      }

      // Fallback: se não encontrou as opções na estrutura esperada, tentar outras estruturas
      if (categoryOptions.length === 0 && classificationOptions.length === 0) {
        // Tentar estrutura alternativa
        options.forEach(option => {
          if (option.type === "dropdown" || option.type === "labels") {
            if (option.name?.toLowerCase().includes("categoria")) {
              const categories = (option.options || option.config?.options || []).map(cat => ({
                value: cat.id || cat.value,
                label: cat.name || cat.label,
                color: cat.color || "#2196F3"
              })); setCategoryOptions(categories);
            } else if (option.name?.toLowerCase().includes("classificação")) {
              const classifications = (option.options || option.config?.options || []).map(cls => ({
                value: cls.id || cls.value,
                label: cls.name || cls.label,
                color: cls.color || "#FF9800"
              })); setClassificationOptions(classifications);
            }
          }
        });
      }
    } catch (err) {
      console.error("Erro ao carregar opções do ClickUp:", err);
      /*
       * A loja nao tem console para verificar. A mensagem diz o que aconteceu
       * e o que fazer; o detalhe tecnico fica no console, para quem for olhar.
       */
      setOptionsError(
        err?.response
          ? "O servidor respondeu com erro ao buscar as categorias."
          : "Não consegui falar com o servidor para buscar as categorias."
      );
    } finally {
      setOptionsLoading(false);
    }
  };

  const loadTickets = async (page = 0, currentFilters = filters) => {
    try {
      // Verificar se merchant está carregado
      if (!merchant || !merchant.id) {
        return;
      }

      setLoading(true); const result = await getSupportTickets(merchant.id, page, pagination.limit, currentFilters);
      setTickets(result.tickets || []);
      const statusList = result?.tickets?.map(ticket => ({ ...ticket.clickupStatus, value: ticket.clickupStatus.status.toUpperCase().replace(/_/g, ' ') })) || [];
      const uniqueStatusList = statusList.filter((status, index, self) =>
        index === self.findIndex(s => s.status === status.status)
      ).sort((a, b) => (a.orderindex > b.orderindex ? 1 : -1));
      setStatusOptions(uniqueStatusList);
      setPagination(result.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        totalPages: 0
      });
      setError(null);
    } catch (err) {
      setError("Erro ao carregar os chamados");
      toast.error("Erro ao carregar os chamados. Verifique sua conexão.", { theme: "colored" });
      console.error("Erro ao carregar tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    try {
      setFormError("");

      const requiredFields = [
        "userName",
        "email",
        "category",
        "classification",
        "title",
        "description",
      ];
      const hasMissingRequiredField = requiredFields.some((field) => !formData[field]);

      if (isSubscriptionCategory() && !formData.cpf) {
        setFormError("Valide o CPF do cliente para chamados de Assinatura.");
        return;
      }

      if (hasMissingRequiredField) {
        setFormError("Preencha todos os campos obrigatórios.");
        return;
      }


      // Validar e-mail antes de criar o ticket
      if (!validateEmail(formData.email)) {
        return;
      }

      if (createTicketLoading) {
        return;
      }

      setCreateTicketLoading(true);

      const ticketData = {
        storeId: merchant?.id,
        cnpj: merchant?.cnpj,
        storeName: merchant?.name?.storeName,
        attachments: formData.attachments,
        ...formData
      };

      const response = await createSupportTicket(ticketData);
      toast.success("Chamado criado com sucesso!", { theme: "colored" });
      handleCloseDialog();
      setFormData(getInitialFormData());
      setEmailError(""); // Limpar erro ao fechar
      loadClickUpOptions();
      loadTickets(0, filters);

    } catch (err) {
      setError("Erro ao criar o chamado")
      toast.error("Erro ao criar o chamado. Tente novamente.", { theme: "colored" })
      console.error("Erro ao criar ticket:", err);
    } finally {
      setCreateTicketLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setNewTicket(null);
    setSelectedTicket(null);
    setActiveTicket(null);
    setCreateTicketLoading(false); // Reset loading state
    setFormData(getInitialFormData());
    setSupportStep("category");
    setFormError("");
    setEmailError(""); // Limpar erro de e-mail
  };

  const handleCloseTicket = (ticket, isResolved = true) => {
    setCloseTicketModal({
      open: true,
      ticket: ticket,
      reason: '',
      loading: false,
      isResolved: isResolved
    });
  };

  const handleConfirmCloseTicket = async (isResolved) => {
    try {
      setCloseTicketModal(prev => ({ ...prev, loading: true }));

      // Chamar API para encerrar o chamado com motivo
      await closeTicket(closeTicketModal.ticket.id, closeTicketModal.reason, isResolved);
      toast.success(isResolved ? "Chamado resolvido com sucesso!" : "Chamado foi reaberto com sucesso!", { theme: "colored" });

      // Atualizar a lista de tickets
      await loadTickets(pagination.page, filters);

      // Fechar os modais
      setCloseTicketModal({ open: false, ticket: null, reason: '', loading: false, isResolved: true });
      handleCloseDialog();

    } catch (error) {
      console.error('Erro ao atualizar o status do chamado:', error);
      toast.error('Erro ao atualizar o status do chamado. Tente novamente.', { theme: "colored" });
    } finally {
      setCloseTicketModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCancelCloseTicket = () => {
    setCloseTicketModal({ open: false, ticket: null, reason: '', loading: false, isResolved: true });
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setFormData({
      ...formData,
      attachments: [...formData.attachments, ...files],
    });
  };

  const handleRemoveAttachment = (index) => {
    const newAttachments = formData.attachments.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      attachments: newAttachments,
    });
  };



  const validateEmail = (email) => {
    if (!email) {
      setEmailError("E-mail é obrigatório");
      return false;
    }

    if (!validator.isEmail(email)) {
      setEmailError("E-mail inválido");
      return false;
    }

    setEmailError("");
    return true;
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData({ ...formData, email });

    // Validar e-mail em tempo real se já foi digitado algo
    if (email) {
      validateEmail(email);
    } else {
      setEmailError("");
    }
  };

  const getSelectedCategory = () => {
    return categoryOptions.find(cat => cat.value === formData.category);
  };

  const isSubscriptionCategory = () => {
    return getSelectedCategory()?.label === "Assinatura";
  };

  const handleCpfSuccess = (cpf) => {
    setFormData(prev => ({
      ...prev,
      cpf
    }));
    setSupportStep("subscription");
  };

  const handleCategoryContinue = () => {
    if (!formData.category) {
      setFormError("Selecione uma categoria para continuar.");
      return;
    }

    setFormError("");
    setSupportStep(isSubscriptionCategory() ? "cpf" : "form");
  };

  const handleCategoryChange = (category) => {
    setFormData(prev => ({
      ...prev,
      category,
      cpf: "",
      subcategory: "",
    }));
    setFormError("");
  };

  const handleOpenSubscriptionForm = (subcategory) => {
    setFormData(prev => ({
      ...prev,
      subcategory,
    }));
    setSupportStep("form");
  };

  const handleBackToCategory = () => {
    setFormData(prev => ({
      ...prev,
      cpf: "",
      subcategory: "",
    }));
    setFormError("");
    setSupportStep("category");
  };

  const handleNextPage = () => {
    if (pagination.hasNextPage) {
      loadTickets(pagination.page + 1, filters);
    }
  };

  const handlePreviousPage = () => {
    if (pagination.hasPreviousPage) {
      loadTickets(pagination.page - 1, filters);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = async () => {
    try {
      setFiltersLoading(true);
      await loadTickets(0, filters);
    } catch (err) {
      setError("Erro ao aplicar filtros");
      toast.error("Erro ao aplicar filtros. Tente novamente.", { theme: "colored" });
      console.error("Erro ao aplicar filtros:", err);
    } finally {
      setFiltersLoading(false);
    }
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      keyword: "",
      status: null,
      category: null
    };
    setFilters(defaultFilters);
    loadTickets(0, defaultFilters);
  };

  if (loading) {
    return (
      <BasePage>
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </BasePage>
    );
  }

  // Mostrar loading enquanto merchant não carrega
  if (merchantLoading) {
    return (
      <BasePage pageTitle={<><Headset className='w-7 h-7' />Suporte</>}>
        <div className={classes.root}>
          <div className={classes.loadingContainer}>
            <CircularProgress size={40} style={{ color: "#F97B18" }} />
            <Typography variant="h6" style={{ marginTop: 16, color: "#6c757d" }}>
              Carregando dados da loja...
            </Typography>
          </div>
        </div>
      </BasePage>
    );
  }

  return (
    <BasePage pageTitle={<><Headset className='w-7 h-7' />Suporte</>}>
      <div className={classes.root}>
        {/* Header com imagem e título */}
        <div className={classes.header}>
          <Box display="flex" alignItems="center">
            <img
              src={suporteImage}
              alt="Central de Suporte"
              className={classes.headerImage}
            />
            <Typography variant="h4" className={classes.title}>
              Central de Suporte
            </Typography>
          </Box>
          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="outlined"
              style={{
                borderColor: "#F97B18",
                color: "#F97B18",
                textTransform: "none",
                marginRight: "10px",
              }}
              startIcon={<HelpOutlineIcon />}
              onClick={() => setFaqOpen(true)}
            >
              FAQ
            </Button>
            <Button
              variant="contained"
              className={classes.newTicketButton}
              startIcon={<PetsIcon style={{ fontSize: 24, fontWeight: 'bold' }} />}
              onClick={() => setNewTicket(true)}
            >
              Novo Chamado
            </Button>
          </Box>
        </div>

        {error && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            {error}
          </Alert>
        )}

        {/* Componente de Filtros */}
        <Filters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          categoryOptions={categoryOptions}
          statusOptions={statusOptions}
          loading={filtersLoading}
        />

        {/* Debug dos filtros */}
        {tickets?.length === 0 ? (
          <Card className={classes.emptyState} style={{
            borderRadius: "16px",
            border: "2px dashed #E0E0E0",
            backgroundColor: "#fafafa"
          }}>
            <Box textAlign="center" padding={4}>
              <Typography variant="h5" color="textSecondary" gutterBottom style={{ fontWeight: 600 }}>
                📋 Nenhum chamado encontrado
              </Typography>
              <Typography variant="body1" color="textSecondary" style={{ marginBottom: 3 }}>
                Parece que você ainda não tem nenhum chamado de suporte.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Clique no botão "Novo Chamado" para criar seu primeiro ticket de suporte
              </Typography>
            </Box>
          </Card>
        ) : (
          <div>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
              <Typography variant="h5" style={{ fontWeight: 600, color: "#2c3e50" }}>
                Seus Chamados
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Clique em um chamado para ver os detalhes
              </Typography>
            </Box>

            <div className={classes.ticketList}>
              {tickets.map((ticket) => (
                <Card
                  key={ticket.id}
                  className={classes.ticketCard}
                  onClick={() => setActiveTicket(ticket.id)}
                  style={{
                    position: "relative"
                  }}
                >
                  {/* Flag de prioridade colorida */}
                  <Box
                    position="absolute"
                    top={0}
                    right={0}
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "20px solid transparent",
                      borderRight: "20px solid transparent",
                      borderTop: `20px solid ${getPriorityFlagColor(ticket.priority)}`,
                      borderBottom: "20px solid transparent"
                    }}
                  />
                  <CardContent style={{ padding: "20px" }}>
                    {/* Header do Card - Layout moderno */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={2}>
                      {/* ID e Título separados - Alinhados à esquerda */}
                      <Box flex={1} paddingRight={2}>
                        <Typography variant="caption" style={{
                          color: "#6c757d",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          marginBottom: "4px",
                          display: "block"
                        }}>
                          ID: {ticket.id}
                        </Typography>
                        <Typography variant="h6" style={{
                          fontWeight: 600,
                          color: "#2c3e50",
                          fontSize: "1.2rem",
                          lineHeight: 1.3
                        }}>
                          {ticket.title?.length > 50 ? `${ticket.title.substring(0, 50)}...` : ticket.title}
                        </Typography>
                      </Box>

                      {/* Status - Canto Superior Direito */}
                      <Box display="flex" alignItems="flex-start" gap={0.5}>
                        <Tooltip
                          title={getStatusDescription(ticket.clickupStatus?.status) || "Status do chamado"}
                          arrow
                          placement="top"
                        >
                          <Chip
                            label={ticket.clickupStatus?.status?.toUpperCase().replace(/_/g, ' ') || 'SEM STATUS'}
                            style={{
                              backgroundColor: ticket.clickupStatus?.color || "#7A7373",
                              color: "#fff",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              height: "24px",
                              marginTop: "10px",
                              borderRadius: "12px",
                              cursor: "help"
                            }}
                            size="small"
                          />
                        </Tooltip>
                        <Tooltip title="Ver FAQ sobre status" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFaqOpen(true);
                            }}
                            style={{
                              marginTop: "6px",
                              padding: "6px",
                              color: "#6c757d",
                              opacity: 0.7,
                              minWidth: "24px",
                              minHeight: "24px",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = "1";
                              e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.04)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = "0.7";
                              e.currentTarget.style.backgroundColor = "transparent";
                            }}
                          >
                            <HelpOutlineIcon style={{ fontSize: "16px" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Descrição do Status do FAQ */}
                    {(() => {
                      const statusDesc = getStatusDescription(ticket.clickupStatus?.status);
                      if (statusDesc) {
                        return (
                          <Typography variant="caption" style={{
                            display: "block",
                            color: "#6c757d",
                            fontSize: "0.75rem",
                            fontStyle: "italic",
                            marginBottom: "8px",
                            lineHeight: 1.4,
                            paddingLeft: "4px"
                          }}>
                            💡 {statusDesc}
                          </Typography>
                        );
                      }
                      return null;
                    })()}

                    {/* Descrição - Limitada */}
                    <Typography variant="body2" color="textSecondary" style={{
                      lineHeight: 1.5,
                      marginBottom: "12px",
                      fontSize: "0.85rem",
                      color: "#6c757d"
                    }}>
                      {ticket.description?.length > 100 ? `${ticket.description.substring(0, 100)}...` : ticket.description}
                    </Typography>

                    {/* Tags Ultra Minimalistas - Alinhamento Perfeito */}
                    <Box
                      display="flex"
                      gap={2}
                      alignItems="center"
                      marginBottom={1}
                      style={{ height: '16px' }} // Altura fixa para consistência
                    >
                      {ticket.classification?.name && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '0.75rem',
                            color: '#8e9aaf',
                            fontWeight: 500,
                            letterSpacing: '0.3px',
                            lineHeight: '16px',
                            height: '16px'
                          }}
                        >
                          <span
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: ticket.classification.color || '#8e9aaf',
                              display: 'block'
                            }}
                          />
                          {ticket.classification.name}
                        </span>
                      )}

                      {ticket.category?.name && (
                        <span
                          style={{
                            marginLeft: '10px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '0.75rem',
                            color: '#8e9aaf',
                            fontWeight: 500,
                            letterSpacing: '0.3px',
                            lineHeight: '16px',
                            height: '16px'
                          }}
                        >
                          <span
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: ticket.category.color || '#8e9aaf',
                              display: 'block'
                            }}
                          />
                          {ticket.category.name}
                        </span>
                      )}
                    </Box>

                    {/* Footer do Card - Layout simplificado */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-end">
                      {/* Informações do usuário - Alinhadas à esquerda */}
                      <Box display="flex" flexDirection="column" gap={0.5} flex={1}>
                        <Box display="flex" alignItems="center">
                          <Typography variant="caption" style={{
                            color: "#6c757d",
                            fontWeight: 500,
                            fontSize: "0.75rem"
                          }}>
                            👤 {ticket.userName?.length > 20 ? `${ticket.userName.substring(0, 20)}...` : ticket.userName}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography variant="caption" style={{
                            color: "#6c757d",
                            fontWeight: 500,
                            fontSize: "0.75rem"
                          }}>
                            📧 {ticket.email?.length > 30 ? `${ticket.email.substring(0, 30)}...` : ticket.email}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Data - Alinhada à direita */}
                      <Box display="flex" alignItems="flex-end" minWidth="100px" justifyContent="flex-end">
                        <Typography variant="caption" style={{
                          color: "#999",
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          textAlign: "right"
                        }}>
                          📅 {formatDate(ticket.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Controles de Paginação */}
            {pagination.totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginTop={4}
                padding={3}
                style={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px",
                  border: "1px solid #e9ecef"
                }}
              >
                <Button
                  onClick={handlePreviousPage}
                  disabled={!pagination.hasPreviousPage}
                  variant="outlined"
                  size="small"
                  style={{
                    marginRight: 16,
                    borderRadius: "8px",
                    borderColor: "#F97B18",
                    color: "#F97B18"
                  }}
                >
                  ← Anterior
                </Button>

                <Box textAlign="center">
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#2c3e50" }}>
                    Página {pagination.page} de {pagination.totalPages}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {pagination.total} tickets no total
                  </Typography>
                </Box>

                <Button
                  onClick={handleNextPage}
                  disabled={!pagination.hasNextPage}
                  variant="outlined"
                  size="small"
                  style={{
                    marginLeft: 16,
                    borderRadius: "8px",
                    borderColor: "#F97B18",
                    color: "#F97B18"
                  }}
                >
                  Próxima →
                </Button>
              </Box>
            )}
          </div>
        )}

        {/* Dialog para criar/visualizar ticket */}
        <Dialog
          open={newTicket}
          onClose={handleCloseDialog}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            style: {
              width: '95vw',
              height: '95vh',
              maxWidth: 'none',
              maxHeight: '95vh',
              margin: '10px',
            }
          }}
        >
          <DialogTitle className={classes.dialogTitle}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              {selectedTicket ? "Detalhes do Chamado" : "Novo Chamado"}
              <IconButton onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent className={selectedTicket ? '' : classes.dialogContent} style={selectedTicket ? { padding: 0, overflow: 'auto' } : { overflow: 'auto' }}>
            {selectedTicket ? (
              <TicketDetails
                ticket={selectedTicket}
                categoryOptions={categoryOptions}
                classificationOptions={classificationOptions}
                onClose={handleCloseDialog}
              />
            ) : supportStep === "cpf" ? (
              <>
                <Box display="flex" justifyContent="flex-start" marginBottom={2}>
                  <Button
                    variant="outlined"
                    onClick={handleBackToCategory}
                    style={{ color: "#F97B18", borderColor: "#F97B18" }}
                  >
                    Voltar para categorias
                  </Button>
                </Box>
                <SearchCPFComponent
                  storeId={merchant?.id}
                  onSuccess={handleCpfSuccess}
                />
              </>
            ) : supportStep === "category" ? (
              <Box maxWidth={640} margin="0 auto" paddingTop={3}>
                <Typography variant="h6" gutterBottom>
                  Selecione a categoria
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {/* A validação de CPF será solicitada apenas para chamados de Assinatura.*/}
                </Typography>
                {formError && (
                  <Alert severity="warning" style={{ marginBottom: 16 }}>
                    {formError}
                  </Alert>
                )}
                <FormControl fullWidth className={classes.formField}>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    required
                    disabled={optionsLoading}
                  >
                    {optionsLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} style={{ marginRight: 8 }} />
                        Carregando categorias...
                      </MenuItem>
                    ) : optionsError ? (
                      <MenuItem disabled>
                        ❌ Erro ao carregar categorias
                      </MenuItem>
                    ) : categoryOptions.length === 0 ? (
                      <MenuItem disabled>
                        ⚠️ Nenhuma categoria encontrada
                      </MenuItem>
                    ) : (
                      categoryOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Box display="flex" justifyContent="flex-end" marginTop={2}>
                  <Button
                    variant="contained"
                    className={classes.newTicketButton}
                    onClick={handleCategoryContinue}
                    disabled={!formData.category || optionsLoading}
                  >
                    Continuar
                  </Button>
                </Box>
              </Box>
            ) : supportStep === "subscription" ? (
              <>
                <Box display="flex" justifyContent="flex-start" marginBottom={2}>
                  <Button
                    variant="outlined"
                    onClick={handleBackToCategory}
                    style={{ color: "#F97B18", borderColor: "#F97B18" }}
                  >
                    Voltar para categorias
                  </Button>
                </Box>
                <SubscriptionSubcategoryCards
                  selectedSubcategory={formData.subcategory}
                  onResolved={handleCloseDialog}
                  onOpenForm={handleOpenSubscriptionForm}
                />
              </>
            ) : (
              <form>
                {/*
                  * Antes isso era uma caixa de "Debug das Opcoes" com a contagem
                  * crua e "verifique o console" — texto de desenvolvedor na tela
                  * da loja, que nao tem console nem como tentar de novo a nao ser
                  * recarregando a pagina inteira.
                  */}
                {!optionsLoading && (optionsError || categoryOptions.length === 0) && (
                  <Alert
                    severity="warning"
                    style={{ marginBottom: 16 }}
                    action={
                      <Button color="inherit" size="small" onClick={loadClickUpOptions}>
                        Tentar de novo
                      </Button>
                    }
                  >
                    {optionsError
                      ? `${optionsError} Sem elas não dá para abrir o chamado.`
                      : "Nenhuma categoria veio do servidor. Sem elas não dá para abrir o chamado."}
                  </Alert>
                )}

                {formError && (
                  <Alert severity="warning" style={{ marginBottom: 16 }}>
                    {formError}
                  </Alert>
                )}

                {/* Texto de orientação para descrição */}
                <Box
                  marginBottom={2}
                  padding={2}
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '4px',
                    borderLeft: '4px solid #F97B18'
                  }}
                >
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>💡 Dica para agilizar seu atendimento:</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Compartilhe o máximo de detalhes possível, como CPF do cliente, o que aconteceu, quando começou e prints ou anexos, se tiver.
                    Você também pode acessar nosso módulo de vídeos — ele pode te ajudar a resolver mais rápido
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nome do Usuário"
                      value={formData.userName}
                      onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                      className={classes.formField}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="E-mail de Contato"
                      type="email"
                      value={formData.email}
                      onChange={handleEmailChange}
                      className={classes.formField}
                      required
                      error={!!emailError}
                      helperText={emailError}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="WhatsApp para Notificação de Movimentação"
                      type="tel"
                      value={formData.whatsapp}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Remove tudo que não é dígito
                        let formatted = value;
                        if (value.length > 0) {
                          if (value.length <= 2) {
                            formatted = `(${value}`;
                          } else if (value.length <= 7) {
                            formatted = `(${value.slice(0, 2)}) ${value.slice(2)}`;
                          } else if (value.length <= 10) {
                            formatted = `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
                          } else {
                            formatted = `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
                          }
                        }
                        setFormData({ ...formData, whatsapp: formatted });
                      }}
                      placeholder="(00) 00000-0000"
                      className={classes.formField}
                      helperText="Opcional: Receba notificações de movimentação do chamado via WhatsApp"
                    />
                  </Grid>
                  {isSubscriptionCategory() && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="CPF"
                        value={formData.cpf}
                        className={classes.formField}
                        required
                        disabled
                      />
                    </Grid>
                  )}
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth className={classes.formField}>
                      <InputLabel>Categoria</InputLabel>
                      <Select
                        value={formData.category}
                        onChange={(e) => {
                          const category = e.target.value;
                          handleCategoryChange(category);
                          const selectedCategory = categoryOptions.find(cat => cat.value === category);
                          if (selectedCategory?.label === "Assinatura") {
                            setSupportStep("cpf");
                          }
                        }}
                        required
                        disabled={optionsLoading}
                      >
                        {optionsLoading ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} style={{ marginRight: 8 }} />
                            Carregando categorias...
                          </MenuItem>
                        ) : optionsError ? (
                          <MenuItem disabled>
                            ❌ Erro ao carregar categorias
                          </MenuItem>
                        ) : categoryOptions.length === 0 ? (
                          <MenuItem disabled>
                            ⚠️ Nenhuma categoria encontrada
                          </MenuItem>
                        ) : (
                          categoryOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth className={classes.formField}>
                      <InputLabel>Classificação</InputLabel>
                      <Select
                        value={formData.classification}
                        onChange={(e) => setFormData({ ...formData, classification: e.target.value })}
                        required
                        disabled={optionsLoading}
                      >
                        {optionsLoading ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} style={{ marginRight: 8 }} />
                            Carregando classificações...
                          </MenuItem>
                        ) : optionsError ? (
                          <MenuItem disabled>
                            ❌ Erro ao carregar classificações
                          </MenuItem>
                        ) : classificationOptions.length === 0 ? (
                          <MenuItem disabled>
                            ⚠️ Nenhuma classificação encontrada
                          </MenuItem>
                        ) : (
                          classificationOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Informações adicionais para categoria Ifood */}
                {(() => {
                  const selectedCategory = categoryOptions.find(cat => cat.value === formData.category);
                  const isIfoodCategory = selectedCategory?.label?.toLowerCase().includes('ifood');

                  if (isIfoodCategory) {
                    return (
                      <Box
                        marginBottom={2}
                        padding={2.5}
                        style={{
                          backgroundColor: '#e3f2fd',
                          border: '1px solid #90caf9',
                          borderRadius: '8px',
                          borderLeft: '4px solid #2196f3'
                        }}
                      >
                        <Typography variant="h6" style={{
                          color: '#1565c0',
                          fontWeight: 600,
                          marginBottom: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8
                        }}>
                          📱 Informações Importantes para Chamados iFood
                        </Typography>
                        <Typography variant="body2" style={{
                          color: '#424242',
                          marginBottom: 16,
                          lineHeight: 1.6
                        }}>
                          Para agilizar a resolução do seu chamado relacionado ao iFood, por favor, inclua as seguintes informações na descrição:
                        </Typography>
                        <Box component="ul" style={{
                          margin: 0,
                          paddingLeft: 20,
                          color: '#424242'
                        }}>
                          <Typography variant="body2" component="li" style={{ marginBottom: 8, lineHeight: 1.6 }}>
                            <strong>Código de barras do produto:</strong> Informe o código de barras (EAN/GTIN) do produto com problema
                          </Typography>
                          <Typography variant="body2" component="li" style={{ marginBottom: 8, lineHeight: 1.6 }}>
                            <strong>Nome do produto:</strong> Nome completo do produto conforme cadastrado
                          </Typography>
                          <Typography variant="body2" component="li" style={{ marginBottom: 8, lineHeight: 1.6 }}>
                            <strong>Valor correto:</strong> Informe qual deveria ser o valor correto do produto
                          </Typography>
                          <Typography variant="body2" component="li" style={{ marginBottom: 8, lineHeight: 1.6 }}>
                            <strong>Estoque correto:</strong> Informe qual deveria ser a quantidade correta em estoque
                          </Typography>
                          <Typography variant="body2" component="li" style={{ marginBottom: 8, lineHeight: 1.6 }}>
                            <strong>Evidências relevantes:</strong> Anexe prints do pedido do iFood, prints do varejo online, ou qualquer outra evidência que ajude a entender o problema
                          </Typography>
                        </Box>
                        <Box
                          marginTop={2}
                          padding={1.5}
                          style={{
                            backgroundColor: '#fff3e0',
                            border: '1px solid #ffb74d',
                            borderRadius: '4px',
                            borderLeft: '3px solid #ff9800'
                          }}
                        >
                          <Typography variant="body2" style={{
                            color: '#e65100',
                            fontSize: '0.875rem',
                            lineHeight: 1.5
                          }}>
                            <strong>💡 Dica:</strong> Quanto mais informações e evidências você fornecer, mais rápido conseguiremos resolver seu problema!
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }
                  return null;
                })()}

                <TextField
                  fullWidth
                  label="Título"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={classes.formField}
                  required
                />

                <TextField
                  fullWidth
                  label="Descrição"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={4}
                  className={classes.formField}
                  required
                  placeholder="Descreva detalhadamente o problema que está enfrentando..."
                />

                {/* Seção de Anexos */}
                <div className={classes.attachmentSection}>
                  <Typography variant="h6" gutterBottom>
                    Anexos
                  </Typography>

                  <input
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    className={classes.fileInput}
                    id="file-upload"
                    multiple
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<AttachFileIcon />}
                      className={classes.uploadButton}
                    >
                      Adicionar Anexos
                    </Button>
                  </label>

                  {formData.attachments?.length > 0 && (
                    <div className={classes.attachmentList}>
                      {formData.attachments.map((file, index) => (
                        <div key={index} className={classes.attachmentItem}>
                          <Typography variant="body2">
                            {file.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveAttachment(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </form>
            )}
          </DialogContent>
          <DialogActions style={{ marginTop: "10px" }}>
            <Button
              onClick={handleCloseDialog}
              style={{ color: "#F97B18", borderColor: "#F97B18" }}
              variant="outlined"
            >
              {selectedTicket ? "Fechar" : "Cancelar"}
            </Button>
            {selectedTicket && (() => {
              const statusText = selectedTicket.clickupStatus?.status?.toLowerCase() || '';
              const isValidationStatus = statusText.includes('validation') ||
                statusText.includes('validação') ||
                statusText.includes('validacao');

              if (isValidationStatus && selectedTicket.clickupStatus.type !== 'closed') {
                return (
                  <>
                    <Tooltip
                      title="O problema ainda persiste? Clique aqui para reabrir o chamado. Nossa equipe voltará a analisar e continuará trabalhando na solução."
                      arrow
                      placement="top"
                    >
                      <Button
                        onClick={() => handleCloseTicket(selectedTicket, false)}
                        variant="contained"
                        className={classes.validationButtonRed}
                        style={{
                          backgroundColor: "#dc3545",
                          color: "#fff",
                          marginRight: 8,
                          fontWeight: 600,
                        }}
                      >
                        Não Resolvido
                      </Button>
                    </Tooltip>
                    <Tooltip
                      title="O problema foi resolvido com sucesso? Clique aqui para confirmar e encerrar o chamado. O chamado será fechado como resolvido."
                      arrow
                      placement="top"
                    >
                      <Button
                        onClick={() => handleCloseTicket(selectedTicket, true)}
                        variant="contained"
                        className={classes.validationButton}
                        style={{
                          backgroundColor: "#28a745",
                          color: "#fff",
                          fontWeight: 600,
                        }}
                      >
                        Resolvido
                      </Button>
                    </Tooltip>
                  </>
                );
              }

              if (selectedTicket.clickupStatus.type !== 'closed') {
                return (
                  <Button
                    onClick={() => handleCloseTicket(selectedTicket)}
                    variant="contained"
                    style={{
                      backgroundColor: "#28a745",
                      color: "#fff",
                    }}
                  >
                    Encerrar Chamado
                  </Button>
                );
              }

              return null;
            })()}
            {!selectedTicket && supportStep === "form" && (
              <Button
                onClick={handleCreateTicket}
                variant="contained"
                className={classes.newTicketButton}
                disabled={!formData.userName || !formData.email || (isSubscriptionCategory() && !formData.cpf) || !formData.category || !formData.classification || !formData.title || !formData.description || optionsLoading || !!emailError || createTicketLoading}
                startIcon={createTicketLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {createTicketLoading ? 'Criando...' : 'Criar Chamado'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Modal de Confirmação para Encerrar Chamado */}
        <Dialog
          open={closeTicketModal.open}
          onClose={handleCancelCloseTicket}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle style={{
            backgroundColor: closeTicketModal.isResolved ? "#e8f5e9" : "#fff3e0",
            borderBottom: `1px solid ${closeTicketModal.isResolved ? "#c8e6c9" : "#ffe0b2"}`,
            color: "#495057"
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: closeTicketModal.isResolved ? '#4caf50' : '#ff9800',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}>
                {closeTicketModal.isResolved ? '✅' : '⚠️'}
              </div>
              {closeTicketModal.isResolved ? 'Confirmar Resolução' : 'Problema Não Resolvido'}
            </Box>
          </DialogTitle>
          <DialogContent style={{ padding: "24px" }}>
            <Typography variant="body1" style={{
              marginBottom: "16px",
              color: "#495057",
              lineHeight: 1.5
            }}>
              {closeTicketModal.isResolved
                ? `Tem certeza que o problema foi resolvido no chamado "${closeTicketModal.ticket?.title}"?`
                : `O problema no chamado "${closeTicketModal.ticket?.title}" não foi resolvido?`
              }
            </Typography>

            <Typography variant="body2" style={{
              marginBottom: "20px",
              color: "#6c757d",
              fontSize: "0.875rem"
            }}>
              {closeTicketModal.isResolved
                ? "O chamado será encerrado como resolvido. Esta ação não pode ser desfeita."
                : "O chamado será reaberto e voltará para 'Em Progresso'. Nossa equipe continuará trabalhando na solução."
              }
            </Typography>

            <TextField
              fullWidth
              label={closeTicketModal.isResolved ? "Observações (opcional)" : "Descreva o problema que ainda persiste *"}
              value={closeTicketModal.reason}
              onChange={(e) => setCloseTicketModal(prev => ({ ...prev, reason: e.target.value }))}
              multiline
              rows={3}
              variant="outlined"
              placeholder={closeTicketModal.isResolved
                ? "Adicione alguma observação sobre a resolução (opcional)..."
                : "Descreva detalhadamente o que ainda não está funcionando..."
              }
              required={!closeTicketModal.isResolved}
              style={{ marginBottom: "16px" }}
              inputProps={{ maxLength: 500 }}
              helperText={`${closeTicketModal.reason.length}/500 caracteres`}
            />
          </DialogContent>
          <DialogActions style={{ padding: "16px 24px" }}>
            <Button
              onClick={handleCancelCloseTicket}
              style={{ color: "#6c757d" }}
              disabled={closeTicketModal.loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => handleConfirmCloseTicket(closeTicketModal.isResolved)}
              variant="contained"
              disabled={(!closeTicketModal.isResolved && !closeTicketModal.reason.trim()) || closeTicketModal.loading}
              style={{
                backgroundColor: closeTicketModal.isResolved ? "#28a745" : "#dc3545",
                color: "#fff",
              }}
              startIcon={closeTicketModal.loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {closeTicketModal.loading
                ? 'Processando...'
                : closeTicketModal.isResolved
                  ? 'Confirmar Resolução'
                  : 'Reabrir Chamado'
              }
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog do FAQ */}
        <Dialog
          open={faqOpen}
          onClose={() => setFaqOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "16px",
            }
          }}
        >
          <DialogTitle className={classes.dialogTitle}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              FAQ - Status dos Chamados
              <IconButton onClick={() => setFaqOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent style={{ padding: 0 }}>
            <FAQ />
          </DialogContent>
          <DialogActions style={{ padding: "16px 24px" }}>
            <Button
              onClick={() => setFaqOpen(false)}
              variant="contained"
              className={classes.newTicketButton}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </BasePage>
  );
}

export default Suporte;
